import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import api from '../api/client';
import { ACTIVE_STATUSES } from '../constants/jobs';

/**
 * Un seul polling des tâches pour toute l'application.
 *
 * Quatre composants interrogeaient chacun `/api/jobs` toutes les trois secondes :
 * le tableau de bord, le panneau flottant, la fiche client et la page des tâches.
 * Ici une seule boucle nourrit tout le monde, et son rythme suit l'activité :
 * 3 s tant qu'une tâche tourne, 30 s sinon, rien du tout quand l'onglet est
 * caché — avec une lecture immédiate dès qu'il revient au premier plan.
 */

const FAST_MS = 3000;
const SLOW_MS = 30000;
/** Assez large pour couvrir chaque consommateur (la page des tâches en montre 50). */
const LIMIT = 50;

const JobsContext = createContext(null);

export function JobsProvider({ children }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const jobsRef = useRef([]);
  const timer = useRef(null);
  const seq = useRef(0);
  const tickRef = useRef(null);

  useEffect(() => {
    let alive = true;

    const hasActive = () => jobsRef.current.some((j) => ACTIVE_STATUSES.includes(j.status));

    const schedule = () => {
      clearTimeout(timer.current);
      if (!alive || document.visibilityState === 'hidden') return;
      timer.current = setTimeout(() => tickRef.current(), hasActive() ? FAST_MS : SLOW_MS);
    };

    // Chaque lecture porte un numéro : une réponse dépassée par une plus récente
    // (rafraîchissement demandé pendant un poll) n'écrase pas l'état.
    const tick = async () => {
      seq.current += 1;
      const mine = seq.current;
      try {
        const list = await api.getJobs({ limit: LIMIT });
        if (!alive || mine !== seq.current) return;
        jobsRef.current = list;
        setJobs(list);
        setError('');
      } catch (e) {
        if (alive && mine === seq.current) setError(e.message);
      } finally {
        if (alive && mine === seq.current) {
          setLoading(false);
          schedule();
        }
      }
    };
    tickRef.current = tick;

    const onVisibility = () => {
      if (document.visibilityState === 'visible') tick();
      else clearTimeout(timer.current);
    };
    document.addEventListener('visibilitychange', onVisibility);
    tick();

    return () => {
      alive = false;
      clearTimeout(timer.current);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  /** Relecture immédiate ; la promesse se résout une fois la liste à jour. */
  const refresh = useCallback(() => (tickRef.current ? tickRef.current() : Promise.resolve()), []);

  const value = useMemo(() => ({ jobs, refresh, loading, error }), [jobs, refresh, loading, error]);

  return <JobsContext.Provider value={value}>{children}</JobsContext.Provider>;
}

/**
 * `{ jobs, refresh, loading, error }` — `loading` ne vaut vrai qu'avant la
 * première réponse ; les relectures suivantes se font sans clignotement.
 */
export default function useJobs() {
  const ctx = useContext(JobsContext);
  if (!ctx) throw new Error('useJobs doit être appelé sous <JobsProvider>.');
  return ctx;
}
